import { useState } from 'react';
import { Box, Collapse, styled } from '@mui/material';
import { Typography, colors } from '@sweep-io/sweep-design';
import { ExpandButtonSimple } from '../../../common/ExpandButtonSimple';
import BetaTag from '../../../common/BetaTag';
import { UserStory } from '../types';

const SectionBox = styled(Box)(() => ({
  padding: '0 0 16px 0',
  borderBottom: `1px solid ${colors.grey[120]}`,
  width: '100%',
}));

const SubSectionBox = styled(Box)(() => ({
  paddingTop: '12px',
}));

interface UserStorySectionProps {
  userStory?: UserStory;
  isExpandedByDefault?: boolean;
}

const camelCaseToTitle = (str: string): string => {
  if (!str) return '';
  return str
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (char) => char.toUpperCase())
    .trim();
};

const SUB_HEADER_PATTERNS = ['Trigger:', 'Actions:', 'Conditions:', 'Fields:', 'Related Objects:'];

const isSubHeader = (item: string): boolean => {
  return SUB_HEADER_PATTERNS.some((pattern) => item.startsWith(pattern));
};

const BulletList = ({ items }: { items: string[] }) => (
  <Box display="flex" width="100%" flexDirection="column" gap={0.5}>
    {items.map((item, index) => {
      const isHeader = isSubHeader(item);

      if (isHeader) {
        return (
          <Box key={index} pt={index > 0 ? 1 : 0}>
            <Typography variant="body" color={colors.grey[700]}>
              {item}
            </Typography>
          </Box>
        );
      }

      return (
        <Box
          key={index}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            pl: 2,
          }}
        >
          <Typography variant="body" color={colors.grey[700]}>
            •
          </Typography>
          <Typography variant="body" color={colors.grey[700]}>
            {item}
          </Typography>
        </Box>
      );
    })}
  </Box>
);

const SubSection = ({ title, content }: { title: string; content: string | string[] }) => {
  if (!content || (Array.isArray(content) && content.length === 0)) {
    return null;
  }

  return (
    <SubSectionBox>
      <Typography variant="body-medium" color={colors.grey[800]}>
        {title}
      </Typography>
      <Box pt={0.5}>
        {Array.isArray(content) ? (
          <BulletList items={content} />
        ) : (
          <Typography variant="body" color={colors.grey[700]}>
            {content}
          </Typography>
        )}
      </Box>
    </SubSectionBox>
  );
};

export const UserStorySection = ({
  userStory,
  isExpandedByDefault = false,
}: UserStorySectionProps) => {
  const [isExpanded, setIsExpanded] = useState(isExpandedByDefault);

  if (!userStory) {
    return null;
  }

  const { description, behavior, data, purpose, testCoverageFor, note } = userStory;

  const hasContent =
    description || behavior?.length || data?.length || purpose || testCoverageFor || note;

  if (!hasContent) {
    return null;
  }

  return (
    <SectionBox>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        onClick={() => setIsExpanded(!isExpanded)}
        sx={{ cursor: 'pointer', width: '100%' }}
      >
        <Box display="flex" gap={1} alignItems="center" width="100%" pr={1}>
          <Typography variant="h5-medium">User Story</Typography> <BetaTag />
        </Box>
        <ExpandButtonSimple buttonSize="tiny" isExpanded={isExpanded} />
      </Box>
      <Collapse in={isExpanded}>
        <Box pt={2} display="flex" width="100%" flexDirection="column">
          {description && (
            <Typography variant="body" color={colors.grey[700]} whiteSpace="pre-wrap">
              {description}
            </Typography>
          )}

          {behavior && behavior.length > 0 && (
            <SubSection title={camelCaseToTitle('behavior')} content={behavior} />
          )}

          {data && data.length > 0 && (
            <SubSection title={camelCaseToTitle('data')} content={data} />
          )}

          {purpose && <SubSection title={camelCaseToTitle('purpose')} content={purpose} />}

          {testCoverageFor && (
            <SubSection title={camelCaseToTitle('testCoverageFor')} content={testCoverageFor} />
          )}

          {note && <SubSection title={camelCaseToTitle('note')} content={note} />}
        </Box>
      </Collapse>
    </SectionBox>
  );
};
